import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, BarChart3, Loader2, ChevronRight } from "lucide-react";
import { API } from "@/App";
import axios from "axios";

const PERIODS = [
  { value: "1Y", label: "1 Year" },
  { value: "3Y", label: "3 Years" },
  { value: "5Y", label: "5 Years" },
  { value: "10Y", label: "10 Years" },
];

const CATEGORIES = [
  "Large Cap", "Mid Cap", "Small Cap", "Multi Cap", "ELSS",
  "Hybrid", "Debt", "Index Fund", "Sectoral", "International"
];

export const TrailingReturnsPage = () => {
  const [period, setPeriod] = useState("3Y");
  const [category, setCategory] = useState("Large Cap");
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchFunds = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/funds/search?q=${category}&limit=30`);
        const filtered = (res.data.funds || [])
          .filter(f => f.planType === "Direct" && f.option === "Growth");
        setFunds(filtered);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFunds();
  }, [category]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl font-bold mb-4">
              Mutual Fund <span className="gold-text">Trailing Returns</span>
            </h1>
            <p className="text-muted-foreground">
              Compare fund returns over different time periods
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card className="bg-card border-border mb-8">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Category</label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Period</label>
                    <Select value={period} onValueChange={setPeriod}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select period" />
                      </SelectTrigger>
                      <SelectContent>
                        {PERIODS.map((p) => (
                          <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    {category} Funds - {period} Returns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rank</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Fund Name</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Risk</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {funds.map((fund, idx) => (
                          <tr key={fund.schemeCode} className="border-b border-border/50 hover:bg-secondary/50">
                            <td className="py-3 px-4">
                              <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                {idx + 1}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <p className="font-medium text-sm line-clamp-1">{fund.schemeName}</p>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">{fund.category}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={`text-xs px-2 py-1 rounded ${
                                fund.risk === "Very High" ? "bg-red-500/10 text-red-500" :
                                fund.risk === "High" ? "bg-orange-500/10 text-orange-500" :
                                "bg-green-500/10 text-green-500"
                              }`}>
                                {fund.risk}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Link to={`/research/${fund.schemeCode}`}>
                                <ChevronRight className="w-4 h-4 text-muted-foreground hover:text-primary" />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
